import React, { useEffect, useState } from 'react';
import {
  ArrowDownTrayIcon,
  ArrowUpOnSquareIcon,
  ShareIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { isIosDevice, isIosSafari, isStandaloneDisplay } from '../../utils/pwa';
import { t } from '../../utils/translations';

const DISMISS_KEY = 'pwa-install-dismissed';

function readDismissed(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

function writeDismissed(): void {
  try {
    localStorage.setItem(DISMISS_KEY, '1');
  } catch {
    /* private mode / blocked storage */
  }
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const InstallPwaBanner: React.FC = () => {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIosHint, setShowIosHint] = useState(false);
  const [dismissed, setDismissed] = useState(readDismissed);

  useEffect(() => {
    if (isStandaloneDisplay() || dismissed) return;

    const onPrompt = (event: Event) => {
      event.preventDefault();
      setDeferred(event as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);

    if (isIosDevice()) {
      setShowIosHint(true);
    }

    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, [dismissed]);

  const dismiss = () => {
    writeDismissed();
    setDismissed(true);
    setDeferred(null);
    setShowIosHint(false);
  };

  const install = async () => {
    if (!deferred) return;
    await deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice.outcome === 'accepted') {
      dismiss();
    }
    setDeferred(null);
  };

  if (dismissed || isStandaloneDisplay()) return null;

  const visible = Boolean(deferred) || showIosHint;
  if (!visible) return null;

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-[calc(1rem+env(safe-area-inset-bottom))] z-50 flex justify-center px-4">
      <div className="pointer-events-auto flex max-w-lg items-start gap-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-lg">
        <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-700">
          {deferred ? (
            <ArrowDownTrayIcon className="h-5 w-5" />
          ) : (
            <ShareIcon className="h-5 w-5" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-900">{t.pwa.installTitle}</p>
          {deferred ? (
            <p className="mt-0.5 text-xs text-slate-500">{t.pwa.installAndroidHint}</p>
          ) : isIosSafari() ? (
            <ol className="mt-1.5 space-y-1.5">
              <li className="flex items-center gap-2 text-xs text-slate-600">
                <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded bg-slate-100 text-[10px] font-semibold text-slate-500">
                  1
                </span>
                {t.pwa.installIosStep1}
                <ArrowUpOnSquareIcon className="h-4 w-4 shrink-0 text-brand-600" aria-hidden="true" />
              </li>
              <li className="flex items-center gap-2 text-xs text-slate-600">
                <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded bg-slate-100 text-[10px] font-semibold text-slate-500">
                  2
                </span>
                {t.pwa.installIosStep2}
              </li>
            </ol>
          ) : (
            <p className="mt-0.5 text-xs text-slate-500">{t.pwa.installIosOtherBrowser}</p>
          )}
          {deferred && (
            <button
              type="button"
              onClick={() => void install()}
              className="mt-2 rounded-full bg-brand-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-700"
            >
              {t.pwa.installButton}
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          aria-label={t.common.close}
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default InstallPwaBanner;
